import React, { useState } from "react";

import { Formik } from "formik";
import {
	Button,
	Confirm,
	Divider,
	Form,
	Header,
	Segment,
} from "semantic-ui-react";

import { useSelector } from "react-redux";

// validators
import productAddForSell from "validators/productAddForSell";
import sellSubmitValidator from "validators/sellSubmit";

// utils
import http from "utilities/http";
import Alerts from "utilities/Alerts";

// custom supplements
import ProductInput from "../Inputs/ProductInput";
import RateAndQuantity from "../Inputs/RateAndQuantity";
import SellReceiptTable from "components/features/SellReceiptTable";
import PdfSegment from "./PdfSegment";
import BatchFormFooter from "./BatchFormFooter";
import BatchMetaInput from "./BatchMetaInput";

function Sell() {
	const { allProducts } = useSelector((state) => state.products);
	const parseProductData = (id) => {
		const product = allProducts.find((item) => item._id.toString() === id);
		return product;
	};

	const [salesData, setSalesData] = useState([]);

	// confirm modal
	const [openConfirm, setOpenConfirm] = useState(false);

	// for pdf
	const [genEstimate, setGenEstimate] = useState(true);
	const [show, setShow] = useState(false);
	const [pdfData, setPdfData] = useState({});

	const [loading, setLoading] = useState(false);

	const addSalesData = (values, handlers) => {
		const alreadyExistingRecord = salesData.find(
			(item) => item.productId.toString() === values.productId
		);

		if (alreadyExistingRecord) {
			const existingQuantity = parseFloat(alreadyExistingRecord.quantity);
			alreadyExistingRecord.quantity =
				parseFloat(values.quantity) + existingQuantity;
			alreadyExistingRecord.rate = values.rate;
			const otherRecord = salesData.filter(
				(item) => item.productId.toString() !== values.productId
			);
			setSalesData([alreadyExistingRecord, ...otherRecord]);
		} else setSalesData([values, ...salesData]);

		setShow(false);

		handlers.resetForm();
		handlers.setFieldValue("buyer", values.buyer);
		handlers.setFieldValue("invoiceNo", values.invoiceNo);
	};

	// confirm modal actions
	const attemptSubmit = () => setOpenConfirm(true);
	const closeConfirm = () => setOpenConfirm(false);

	const finalSubmit = async (values, handlers) => {
		try {
			const { buyer, invoiceNo, paid } = values;
			const particulars = [];

			setOpenConfirm(false);

			// prepare batch request
			for (const sd of salesData)
				particulars.push(
					http({
						method: "POST",
						url: "/stock/sell",
						data: {
							buyer,
							invoiceNo,
							...sd,
							paid,
						},
					})
				);

			// deploy batch
			setLoading(true);
			await processRequest(particulars);

			// on batch succeed
			if (genEstimate) {
				setPdfData({
					values,
					data: salesData.map((item) => ({
						name:
							parseProductData(item.productId)?.name ??
							"not specified",
						quantity: item.quantity,
						rate: item.rate,
					})),
				});
				setTimeout(() => {
					setShow(true);
				}, 300);
			}

			handlers.resetForm();
			setSalesData([]);
		} catch (ex) {
			Alerts.httpException(ex);
		} finally {
			setLoading(false);
		}
	};

	// do the dunk duh
	const processRequest = (particulars) =>
		Promise.all([particulars])
			.then((responses) => {
				Promise.resolve(responses);
			})
			.catch((ex) => Promise.reject(ex));

	return (
		<Segment basic loading={loading}>
			<Formik
				initialValues={{
					productId: "",
					quantity: "",
					rate: "",
				}}
				onSubmit={addSalesData}
				validationSchema={productAddForSell}
			>
				{({ handleSubmit }) => (
					<Form onSubmit={handleSubmit}>
						<Header as="h2">Add Sales Record</Header>
						<Segment>
							<ProductInput />
							<RateAndQuantity forSale />
							<Button primary type="submit">
								Add Record
							</Button>
						</Segment>
					</Form>
				)}
			</Formik>

			<Divider hidden />

			<Formik
				initialValues={{ buyer: "", invoiceNo: "", paid: false }}
				onSubmit={attemptSubmit}
				validationSchema={sellSubmitValidator}
			>
				{({
					values,
					errors,
					touched,
					handleChange,
					handleSubmit,
					isSubmitting,
					...rest
				}) => (
					<Form onSubmit={handleSubmit}>
						<Segment loading={isSubmitting}>
							<BatchMetaInput />
							<SellReceiptTable
								{...{
									sData: salesData,
									setSData: setSalesData,
								}}
							/>
							<BatchFormFooter
								disabled={!salesData.length}
								{...{ genEstimate, setGenEstimate }}
							/>
							<PdfSegment show={show} pdfData={pdfData} />
							<Confirm
								open={openConfirm}
								onCancel={closeConfirm}
								onConfirm={() =>
									finalSubmit(values, { ...rest })
								}
							/>
						</Segment>
					</Form>
				)}
			</Formik>
		</Segment>
	);
}

export default Sell;
